// @flow

import * as React from 'react';
import { Button } from '@material-ui/core';
import { Box } from 'react-system';
import { graphql, useFragment, useMutation } from 'react-relay';
import { useLocale } from '../../hooks/locale';
import { useRouter } from '../../controls/router/index';
import { Link } from '../../controls/link/index';
import {
  BubblesSeq,
  BubbleBox,
  BubbleLoadingAnim,
} from '../../controls/bubble';
import { InlineMarked } from '../../controls/marked';
import { PhoneVerification } from '../../shared/phone-verification';
import type { developmentVerificationStep_root$key } from './__generated__/developmentVerificationStep_root.graphql';
import type { developmentVerificationStep_form$key } from './__generated__/developmentVerificationStep_form.graphql';
import type { developmentVerificationStepCompleteMutation } from './__generated__/developmentVerificationStepCompleteMutation.graphql';

type Props = {|
  root: developmentVerificationStep_root$key,
  form: developmentVerificationStep_form$key,
|};

export const DevelopmentVerificationStep = (props: Props): React.Node => {
  const { me } = useFragment(
    graphql`
      fragment developmentVerificationStep_root on Query {
        me {
          id
        }
      }
    `,
    props.root,
  );
  const form = useFragment(
    graphql`
      fragment developmentVerificationStep_form on DevelopmentRegistrationForm {
        id
        verified
        completed
      }
    `,
    props.form,
  );
  const [completeDevelopmentRegistration] =
    useMutation<developmentVerificationStepCompleteMutation>(
      graphql`
        mutation developmentVerificationStepCompleteMutation(
          $input: CompleteDevelopmentRegistrationInput!
        ) {
          completeDevelopmentRegistration(input: $input) {
            developmentRegistrationForm {
              ...developmentVerificationStep_form
              step
            }
          }
        }
      `,
    );

  const [processingAndTransitioning, setProcessingAndTransitioning] =
    React.useState(false);
  const { t } = useLocale();
  const router = useRouter();
  const runCompletion = () => {
    setProcessingAndTransitioning(true);
    if (form.completed) {
      router.push({
        pathname: '/development-registration',
        query: { id: form.id, step: '4' },
      });
    } else {
      completeDevelopmentRegistration({
        variables: {
          input: {
            id: form.id,
          },
        },
        onCompleted: () => {
          router.push({
            pathname: '/development-registration',
            query: { id: form.id, step: '4' },
          });
        },
      });
    }
  };

  return (
    <>
      {me == null ? (
        <Link
          href={{
            pathname: '/development-registration',
            query: { id: form.id, step: '1' },
          }}
        >
          {({ href, onClick }) => (
            <Button color="primary" component="a" href={href} onClick={onClick}>
              {t('backToLoginStep')}
            </Button>
          )}
        </Link>
      ) : processingAndTransitioning ? (
        <Box pt="100px" pb="200px" css={{ textAlign: 'center' }}>
          <BubbleLoadingAnim />
          <p css={{ marginTop: 20 }}>
            {t('developmentRegistrationInProgress')}
          </p>
        </Box>
      ) : form.verified ? (
        <BubblesSeq animate={form.completed === false}>
          <BubbleBox>
            <InlineMarked>{t('developmentVerificationCompleted')}</InlineMarked>
          </BubbleBox>
          <Box py={2}>
            <Button color="primary" onClick={runCompletion}>
              {t('next')}
            </Button>
          </Box>
        </BubblesSeq>
      ) : (
        <BubblesSeq animate={form.completed === false}>
          <BubbleBox>
            <InlineMarked>{t('developmentVerificationRequired')}</InlineMarked>
          </BubbleBox>
          <Box py={2}>
            <PhoneVerification
              developmentRegistrationFormId={form.id}
              onComplete={runCompletion}
              primary={true}
            />
          </Box>
        </BubblesSeq>
      )}
    </>
  );
};
